import React from 'react';
import Typography from '@mui/material/Typography';
import { styled, keyframes } from '@mui/system';
import highlightBrand from 'helpers/highlightBrand';
import { getLanguage } from 'translation';
import useIntersectionObserver from 'helpers/useIntersectionObserver';
import { IS_SNAP } from 'variables/main';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Background = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.textPrimary.main
}));

const ServicesWrapper = styled('div')(({ theme }) => ({
  maxWidth: 1030,
  margin: '0 auto',
  padding: '80px 0',
  boxSizing: 'border-box',
  [theme.breakpoints.down('lg')]: {
    padding: 64,
    maxWidth: '100%'
  },
  [theme.breakpoints.down('md')]: {
    padding: 32
  },
  [theme.breakpoints.down('sm')]: {
    padding: 24
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.white.main,
  marginBottom: 48,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  [theme.breakpoints.down('lg')]: {
    display: 'block'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
    lineHeight: '41.4px',
    marginBottom: 32
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '36.4px',
    marginBottom: 32
  }
}));

const TitleDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.65)',
  position: 'relative',
  top: 5,
  paddingLeft: 50,
  [theme.breakpoints.down('sm')]: {
    fontSize: 18,
    paddingLeft: 0,
    lineHeight: '27px',
    marginTop: 15,
    position: 'static'
  }
}));

const List = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '40px 48px',
  [theme.breakpoints.down('md')]: {
    gap: '32px 32px'
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: 32
  }
}));

const ListItem = styled('li', {
  shouldForwardProp: (prop) => prop !== 'isVisible' && prop !== 'delay'
})(({ isVisible, delay }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  maxWidth: 518,
  position: 'relative',
  paddingLeft: 20,
  borderLeft: '2px solid rgba(185, 248, 51, 0.2)',
  transition: 'border-color 0.4s ease',
  ...(!IS_SNAP && { opacity: 0 }),
  ...(isVisible && {
    ...(!IS_SNAP && { animation: `${fadeInUp} 0.6s ease-out ${delay}s both` }),
    borderColor: 'rgba(185, 248, 51, 0.5)'
  }),
  '& a': {
    textDecoration: 'none'
  }
}));

const NumberIndicator = styled('span')(() => ({
  fontFamily: 'Unbounded',
  fontSize: 13,
  fontWeight: 600,
  color: 'rgba(185, 248, 51, 0.7)',
  marginBottom: 12,
  letterSpacing: '0.05em'
}));

const ListItemText = styled(Typography)(({ theme }) => ({
  color: theme.palette.white.main,
  marginBottom: 8,
  [theme.breakpoints.down('md')]: {
    fontSize: 20,
    lineHeight: '28px'
  }
}));

const ListItemDescription = styled(Typography)(() => ({
  color: 'rgba(255, 255, 255, 0.65)',
  lineHeight: '26px'
}));

const Services = ({ texts }) => {
  const lang = getLanguage();
  const [ref, isVisible] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
    threshold: 0.15
  });

  const items = [
    { header: texts?.value_1_header?.[lang], text: texts?.value_1_text?.[lang] },
    { header: texts?.value_2_header?.[lang], text: texts?.value_2_text?.[lang] },
    { header: texts?.value_3_header?.[lang], text: texts?.value_3_text?.[lang] },
    { header: texts?.value_4_header?.[lang], text: texts?.value_4_text?.[lang] },
    { header: texts?.value_5_header?.[lang], text: texts?.value_5_text?.[lang] },
    { header: texts?.value_6_header?.[lang], text: texts?.value_6_text?.[lang] }
  ];

  return (
    <Background>
      <ServicesWrapper ref={ref}>
        <Title variant="h2">
          <span>{highlightBrand(texts?.value_headline?.[lang])}</span>
          <TitleDescription variant="body1">{texts?.value_subheadline?.[lang]}</TitleDescription>
        </Title>
        <List>
          {items.map((item, index) => (
            <ListItem key={index} isVisible={isVisible} delay={0.1 * index}>
              <NumberIndicator>{String(index + 1).padStart(2, '0')}</NumberIndicator>
              <ListItemText variant="h3">{item.header}</ListItemText>
              <ListItemDescription variant="body1">{item.text}</ListItemDescription>
            </ListItem>
          ))}
        </List>
      </ServicesWrapper>
    </Background>
  );
};

export default Services;
